import { useState, useCallback, useEffect, useRef } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../redux/store';
import { addToCart, decreaseQuantity, deleteCart, resetCart } from '../../../redux/slices/product';
import axios from '../../../utils/axios';
import { useAuthContext } from '../../../auth/useAuthContext';
import { useLiveAPI } from '../../../hooks/gemini-live/useLiveAPI';
import { useWakeWord } from '../../../hooks/gemini-live/useWakeWord';
import { ExtractedItem, TrioDetails, CompletedTrioItem, OrderStepType, LastOrderResponse } from '../types/voiceOrderingTypes';
import { publishConversationSnapshot } from '../../../hooks/useVoiceStatusSocket';

// Mettre à true UNIQUEMENT pour tester le jour de paie hors jeudi. Remettre à false en production.
const SIMULATE_PAYDAY = false; 

// Fenêtre (en heures) pour le calcul du best-seller. 2 en prod ; augmenter (ex: 168 = 7j) pour tester avec peu de données.
const BESTSELLER_HOURS = 2;

// Fenêtre (en jours) pendant laquelle un produit est considéré "nouveau".
const NEW_PRODUCT_DAYS = 14;

interface UseGeminiVoiceOrderingProps {
  products: any[];
  cart: any[];
  isEnabled?: boolean;
  trioOptionsMap: Map<string, any>;
}

export function useGeminiVoiceOrdering({ products, cart, isEnabled = false, trioOptionsMap }: UseGeminiVoiceOrderingProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuthContext();
  const [isOrderActive, setIsOrderActive] = useState(false);
  const [extractedData, setExtractedData] = useState<{ items: ExtractedItem[] }>({ items: [] });
  const [trioItems, setTrioItems] = useState<CompletedTrioItem[]>([]);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [isConfirmingOrder, setIsConfirmingOrder] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'trio_selection' | 'agent_requested' | 'agent_connected' | 'ai_resumed' | 'confirming'>('idle');
  const [trioSelection, setTrioSelection] = useState<any>(null);
  const [isAgentRequested, setIsAgentRequested] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const agentTimerRef = useRef<any>(null);
  const currentOrderIdRef = useRef<number | null>(null);
  const livekitRoomRef = useRef<Room | null>(null);
  const [instructions, setInstructions] = useState<string>("Dites 'Commencer la commande' pour activer l'assistant.");
  const [currentTranscript, setCurrentTranscript] = useState("");
  const [accumulatedTranscript, setAccumulatedTranscript] = useState("En attente de démarrage...");

  const [upsellRules, setUpsellRules] = useState<any[]>([]);
  const [pendingUpsell, setPendingUpsell] = useState<any>(null);
  const [proposedRuleIds, setProposedRuleIds] = useState<Set<number>>(new Set());
  const [nextAiStepType, setNextAiStepType] = useState<OrderStepType>('SIMPLE_ORDER');
  const [steps, setSteps] = useState<any[]>([]);

  const sessionIdRef = useRef<string>(crypto?.randomUUID?.() ?? `${Date.now()}`);
  const sessionStartedAtRef = useRef<string>(new Date().toISOString());
  const carPlateRef = useRef<string>('');
  const lastTranscriptRef = useRef<string>("");
  const userSpeechBufferRef = useRef<string>("");
  const aiBufferRef = useRef<string>("");
  const aiBufferTimeoutRef = useRef<any>(null);
  // Indique si le tour de parole client courant a déjà été enregistré (évite les doublons
  // entre la capture via outil et la capture libre au début de la réponse IA).
  const customerTurnCommittedRef = useRef<boolean>(false);
  // Contexte météo de la session (ex: "pluie légère, 8°C"), injecté dans le prompt et le tracking
  const meteoContextRef = useRef<string>("");
  const handleWakeWordRef = useRef<() => void>(() => {});
  const extractedDataRef = useRef<{ items: ExtractedItem[] }>({ items: [] });
  const nextAiStepTypeRef = useRef<OrderStepType>('SIMPLE_ORDER');
  const lastOrderDataRef = useRef<any>(null);
  const pendingRepeatRef = useRef(false);
  const [lastOrderData, setLastOrderData] = useState<LastOrderResponse | null>(null);
  const [showRepeatModal, setShowRepeatModal] = useState(false);

  // Track transcript in a ref to use inside callbacks without closure issues
  useEffect(() => {
    lastTranscriptRef.current = currentTranscript;
  }, [currentTranscript]);
  useEffect(() => { extractedDataRef.current = extractedData; }, [extractedData]);
  useEffect(() => { nextAiStepTypeRef.current = nextAiStepType; }, [nextAiStepType]);
  useEffect(() => { lastOrderDataRef.current = lastOrderData; }, [lastOrderData]);

  const appendStep = useCallback((transcript: string, orderJson: any, type: OrderStepType = 'SIMPLE_ORDER', role: 'CUSTOMER' | 'AI' = 'CUSTOMER') => {
    const newStepBase = {
      sessionId: sessionIdRef.current,
      transcript: transcript || (role === 'CUSTOMER' ? 'Commande vocale...' : 'Réponse assistant...'),
      orderJson: JSON.stringify(orderJson),
      stepType: type,
      speakerRole: role,
      latencyMs: 0,
      createdAt: new Date().toISOString().replace('Z', '')
    };

    setSteps(prev => [...prev, { ...newStepBase, stepIndex: prev.length }]);
  }, []);

  // Flush any pending AI buffer synchronously — call before inserting a CUSTOMER step
  const flushAiBuffer = useCallback(() => {
    if (aiBufferTimeoutRef.current) {
      clearTimeout(aiBufferTimeoutRef.current);
      aiBufferTimeoutRef.current = null;
    }
    const buffered = aiBufferRef.current.trim();
    if (buffered) {
      appendStep(buffered, extractedDataRef.current, nextAiStepTypeRef.current, 'AI');
      setNextAiStepType('SIMPLE_ORDER');
      aiBufferRef.current = '';
    }
  }, [appendStep]);

  // Initialize Gemini API with the specialized Native Audio model
  // La voix est configurée par sous-organisation (champ voiceName renvoyé par /users/my-account
  // via useAuthContext). Fallback "Aoede" = ancienne voix codée en dur.
  const { connect, disconnect, isConnected, isProcessing, sendToolResponse } = useLiveAPI(
    "models/gemini-3.1-flash-live-preview",
    undefined,
    user?.voiceName || "Aoede",
    setCurrentTranscript
  );

  const stopListeningRef = useRef<() => void>(() => { });

  const handleGeminiToolCall = useCallback(async (event: any) => {
    const { name, args, session } = event.detail;

    // Commit any pending AI speech before inserting the customer step — preserves chronological order
    flushAiBuffer();


    // Fallback: Si le transcript est vide ou trop fragmentaire, on construit une phrase descriptive basée sur l'outil
    const fragmentsToIgnore = [
      'chose', 'place', 'commande', '$', '?', '...', 'autre', 'chose ?', 'place ?', 'commande ?',
      'petit', 'peu', 'petit peu', 'un petit peu', 'un peu', 'autre chose ?', 'dollars ?', ' dollars',
      'oui.', 'non.', 'ok', 'merci', 'bonjour', 'salut'
    ];

    // On privilégie d'abord ce que le client a VRAIMENT dit
    let transcriptFallback = userSpeechBufferRef.current.trim();

    // Si le buffer est vide ou fragmentaire, on utilise l'ancienne logique de secours
    const lowerTranscript = transcriptFallback?.toLowerCase() || '';
    const isFragment = !transcriptFallback ||
      transcriptFallback.length < 3 ||
      fragmentsToIgnore.some(f => lowerTranscript === f || lowerTranscript.includes(f));

    if (isFragment) {
      if (name === "ajouter_produit_panier") transcriptFallback = `Ajouter ${args.quantite || 1} ${args.nom_produit}`;
      else if (name === "supprimer_produit_panier") transcriptFallback = `Supprimer ${args.nom_produit}`;
      else if (name === "verifier_options_produit") transcriptFallback = `Quelles sont les options pour ${args.nom_produit} ?`;
      else if (name === "consulter_panier") transcriptFallback = `Consulter mon panier`;
      else if (name === "terminer_commande") transcriptFallback = `Terminer ma commande`;
      else if (name === "tracker_reponse_upsell") transcriptFallback = args.accepte ? "Oui" : "Non";
      else if (name === "request_agent") transcriptFallback = `Je voudrais parler à un agent`;
    }

    // Une fois l'outil appelé, on considère que le tour utilisateur est fini -> on vide le buffer
    // et on marque le tour comme enregistré (la réponse IA ne doit pas le ré-enregistrer).
    userSpeechBufferRef.current = "";
    customerTurnCommittedRef.current = true;

    console.log(`🤖 Gemini appel d'outil: "${name}"`, args);

    let result = {};

    try {
      if (name === "ajouter_produit_panier") {
        setInstructions("Ajout du produit...");

        const searchName = args.nom_produit ? args.nom_produit.toLowerCase() : '';

        const foundProduct = products.find(p => {
          const nameMatch = p.name?.toLowerCase().includes(searchName);
          const transMatch = p.translations?.some((t: any) => t?.value?.toLowerCase().includes(searchName));
          return nameMatch || transMatch;
        });

        if (foundProduct) {
          let basePrice = Number(foundProduct.price) || 0;
          let selectionsList: any[] = [];

          if (Array.isArray(args.options_choisies) && args.options_choisies.length > 0) {
            const trioData = trioOptionsMap.get(foundProduct.id);

            if (trioData && trioData.product) {
              const trioPrice = Number(trioData.product.trioPrice ?? 0);
              if (trioPrice > 0) basePrice = trioPrice;

              selectionsList = args.options_choisies.map((opt: any) => {
                let realPrice = 0;
                let realGroupId = 0;
                if (trioData.option_groups) {
                  const foundGroup = trioData.option_groups.find((group: any) =>
                    group.items.some((i: any) => String(i.id) === String(opt.id) || i.name?.toLowerCase() === opt.nom_option?.toLowerCase())
                  );
                  if (foundGroup) {
                    const item = foundGroup.items.find((i: any) => String(i.id) === String(opt.id) || i.name?.toLowerCase() === opt.nom_option?.toLowerCase());
                    if (item) {
                      realPrice = Number(item.price ?? 0);
                      realGroupId = Number(foundGroup.id ?? 0);
                    }
                  }
                }
                return {
                  optionItemId: Number(opt.id) || 0,
                  name: opt.nom_option,
                  price: realPrice,
                  groupId: realGroupId
                };
              });
            } else {
              selectionsList = args.options_choisies.map((opt: any) => ({
                optionItemId: Number(opt.id) || 0,
                name: opt.nom_option,
                price: 0,
                groupId: 0
              }));
            }
          }

          const totalItemPrice = basePrice + selectionsList.reduce((sum: number, opt: any) => sum + opt.price, 0);
          const optionText = selectionsList.length > 0 ? ` | ${selectionsList.map((s: any) => s.name).join(", ")}` : "";

          // 🔥 UPSELL ENGINE : Pre-check for REPLACEMENT
          let suggestionFound: any = null;
          try {
            const userResponse = await axios.get('/api/v1/users/my-account');
            const subOrgId = userResponse.data?.subOrgId;
            const currentTotal = extractedData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

            const upsellResp = await axios.get(`/api/v1/upsell/resolve/${subOrgId}/${foundProduct.id}`, {
              params: { cartTotal: currentTotal, moment: 'AFTER_ADD_ITEM' }
            });

            if (upsellResp.data && upsellResp.data.ruleType === 'REPLACEMENT') {
              suggestionFound = upsellResp.data;
            }
          } catch (err) {
            console.warn("Pre-check upsell failed", err);
          }

          if (suggestionFound) {
            result = {
              status: "hold",
              message: "Produit en rupture de stock, suggestion de remplacement.",
              suggestion_upsell: {
                type: suggestionFound.ruleType,
                produit_id: suggestionFound.suggestedProductId,
                produit_nom: suggestionFound.suggestedProductName,
                prix: suggestionFound.suggestedPrice,
                phrase: suggestionFound.customPhrase || `Désolé, nous n'avons plus de ${foundProduct.name}. Voulez-vous un ${suggestionFound.suggestedProductName} à la place ?`
              }
            };
            setPendingUpsell({
              ruleId: suggestionFound.ruleId,
              triggerProductId: foundProduct.id,
              suggestedProductId: suggestionFound.suggestedProductId,
              suggestedPrice: suggestionFound.suggestedPrice
            });
            setProposedRuleIds(prev => new Set(prev).add(suggestionFound.ruleId));

            // Mark next AI speech as suggestion
            setNextAiStepType('UPSELL_SUGGESTION');

            setInstructions(`Remplacement proposé pour ${foundProduct.name}`);
            setPhase('idle');
            setTrioSelection(null);

            sendToolResponse([{
              id: (event.detail as any).id || event.detail.callId,
              name,
              response: { result }
            }]);
            return;
          }

          dispatch(addToCart({ ...foundProduct, price: totalItemPrice, quantity: args.quantite || 1, name: `${foundProduct.name}${optionText}` }));

          let newExtractedItems: ExtractedItem[] = [];
          setExtractedData(prev => {
            const existing = prev.items.find(i => i.productId === foundProduct.id && JSON.stringify(i.selections) === JSON.stringify(selectionsList));
            if (existing) {
              newExtractedItems = prev.items.map(i => i.productId === foundProduct.id && JSON.stringify(i.selections) === JSON.stringify(selectionsList) ? { ...i, quantity: i.quantity + (args.quantite || 1) } : i);
            } else {
              newExtractedItems = [...prev.items, {
                productId: foundProduct.id,
                productName: `${foundProduct.name}${optionText}`,
                size: basePrice,
                price: totalItemPrice,
                quantity: args.quantite || 1,
                addOns: [],
                selections: selectionsList.length > 0 ? selectionsList : undefined,
                specialInstructions: null,
                error: []
              }];
            }
            return { items: newExtractedItems };
          });

          // PERSIST STEP
          appendStep(transcriptFallback, { items: newExtractedItems }, 'SIMPLE_ORDER');

          result = { status: "success", article: foundProduct.name, quantite: args.quantite || 1, options_ajoutees: selectionsList };

          // 🔥 UPSELL ENGINE : Resolve suggestion (AFTER_ADD_ITEM)
          try {
            const userResponse = await axios.get('/api/v1/users/my-account');
            const subOrgId = userResponse.data?.subOrgId;
            const currentTotal = newExtractedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

            const upsellResp = await axios.get(`/api/v1/upsell/resolve/${subOrgId}/${foundProduct.id}`, {
              params: { cartTotal: currentTotal, moment: 'AFTER_ADD_ITEM' }
            });

            if (upsellResp.data) {
              const suggestion = upsellResp.data;

              // Check if already proposed to avoid loops
              if (!proposedRuleIds.has(suggestion.ruleId)) {
                (result as any).suggestion_upsell = {
                  type: suggestion.ruleType,
                  produit_id: suggestion.suggestedProductId,
                  produit_nom: suggestion.suggestedProductName,
                  prix: suggestion.suggestedPrice,
                  phrase: suggestion.customPhrase || `Voulez-vous ajouter ${suggestion.suggestedProductName} pour ${suggestion.suggestedPrice}$ ?`
                };

                setPendingUpsell({
                  ruleId: suggestion.ruleId,
                  triggerProductId: foundProduct.id,
                  suggestedProductId: suggestion.suggestedProductId,
                  suggestedPrice: suggestion.suggestedPrice
                });

                setProposedRuleIds(prev => new Set(prev).add(suggestion.ruleId));

                // Mark next AI speech as suggestion
                setNextAiStepType('UPSELL_SUGGESTION');
              }
            }
          } catch (upsellErr) {
            console.warn("Upsell resolution failed", upsellErr);
          }

          setInstructions(`Produit ajouté : ${foundProduct.name} ${optionText}`);
          setPhase('idle');
          setTrioSelection(null);
        } else {
          result = { status: "error", message: "Produit non trouvé au menu" };
          setInstructions(`Produit non trouvé : ${args.nom_produit}`);
        }
      } else if (name === "supprimer_produit_panier") {
        setInstructions("Suppression du produit...");
        const searchName = args.nom_produit ? args.nom_produit.toLowerCase() : '';
        const foundProduct = products.find(p => {
          const nameMatch = p.name?.toLowerCase().includes(searchName);
          const transMatch = p.translations?.some((t: any) => t?.value?.toLowerCase().includes(searchName));
          return nameMatch || transMatch;
        });

        if (foundProduct) {
          const qtyToRemove = args.quantite || 1;
          const cartItem = cart.find(c => c.id === foundProduct.id);

          if (!cartItem) {
            result = { status: "error", message: "Ce produit n'est pas dans le panier." };
          } else {
            let updatedItems: ExtractedItem[] = [];
            if (cartItem.quantity <= qtyToRemove) {
              dispatch(deleteCart(foundProduct.id));
              setExtractedData(prev => {
                updatedItems = prev.items.filter(i => i.productId !== foundProduct.id);
                return { items: updatedItems };
              });
              result = { status: "success", message: "Produit entièrement retiré du panier." };
            } else {
              Array.from({ length: qtyToRemove }).forEach(() => {
                dispatch(decreaseQuantity(foundProduct.id));
              });
              setExtractedData(prev => {
                updatedItems = prev.items.map(i => i.productId === foundProduct.id ? { ...i, quantity: i.quantity - qtyToRemove } : i);
                return { items: updatedItems };
              });
              result = { status: "success", message: "Quantité réduite." };
            }
            // PERSIST STEP
            appendStep(transcriptFallback, { items: updatedItems }, 'CORRECTION');
          }
        } else {
          result = { status: "error", message: "Produit non trouvé" };
        }
      } else if (name === "verifier_options_produit") {
        setInstructions("Vérification des options du produit...");
        const searchName = args.nom_produit ? args.nom_produit.toLowerCase() : '';
        const foundProduct = products.find(p => {
          const nameMatch = p.name?.toLowerCase().includes(searchName);
          const transMatch = p.translations?.some((t: any) => t?.value?.toLowerCase().includes(searchName));
          return nameMatch || transMatch;
        });

        if (foundProduct) {
          const trioData = trioOptionsMap.get(foundProduct.id);

          if (trioData && trioData.option_groups && trioData.option_groups.length > 0) {
            setPhase('trio_selection');
            setTrioSelection({
              trioDetails: {
                product: {
                  id: foundProduct.id,
                  name: foundProduct.name,
                  trioPrice: trioData.product?.trioPrice ?? 0
                },
                option_groups: trioData.option_groups
              },
              currentGroupIndex: 0,
              selections: {},
              isLoading: false
            });
            const optionsStr = trioData.option_groups.map((g: any) =>
              `Groupe: ${g.name} (Obligatoire: ${g.required ? 'Oui' : 'Non'}, Choix multiple: ${g.multiple ? 'Oui' : 'Non'}). Options: ${g.items.map((i: any) => `${i.name} (ID: ${i.id})`).join(", ")}`
            ).join(" | ");

            result = { status: "success", product_id: foundProduct.id, options_disponibles: optionsStr };
          } else {
            result = { status: "success", product_id: foundProduct.id, message: "Ce produit n'a aucune option ou choix." };
          }
          // PERSIST STEP (Inquiry)
          appendStep(transcriptFallback, { items: extractedData.items }, 'SIMPLE_ORDER');
        } else {
          result = { status: "error", message: "Produit non trouvé au menu." };
        }
      } else if (name === "consulter_panier") {
        setInstructions("Consultation du panier...");

        if (extractedData.items.length === 0) {
          result = { status: "success", message: "Le panier est actuellement vide." };
        } else {
          const subtotal = extractedData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
          const itemsList = extractedData.items.map(item => `${item.quantity}x ${item.productName} (${item.price * item.quantity}$)`).join(", ");

          result = {
            status: "success",
            contenu: itemsList,
            sous_total: subtotal,
            message: `Le panier contient ${extractedData.items.length} lignes d'articles pour un sous-total de ${subtotal}$. (Taxes non incluses)`
          };
        }
        // PERSIST STEP (Consultation)
        appendStep(transcriptFallback, { items: extractedData.items }, 'SIMPLE_ORDER');
      } else if (name === "tracker_reponse_upsell") {
        const { accepte, factor, nom_produit } = args;
        console.log(`Upsell ${accepte ? 'ACCEPTED' : 'DECLINED'} (factor=${factor || 'RULE'})`);

        if (pendingUpsell) {
          try {
            const userResponse = await axios.get('/api/v1/users/my-account');
            const subOrgId = userResponse.data?.subOrgId;

            await axios.post('/api/v1/upsell/events', {
              sessionId: sessionIdRef.current,
              organizationId: subOrgId,
              upsellRuleId: pendingUpsell.ruleId,
              triggerProductId: pendingUpsell.triggerProductId,
              suggestedProductId: pendingUpsell.suggestedProductId,
              suggestedPrice: pendingUpsell.suggestedPrice,
              result: accepte ? 'ACCEPTED' : 'DECLINED',
              weatherContext: meteoContextRef.current || null,
              factor: 'RULE'
            });

            appendStep(transcriptFallback, {}, accepte ? 'UPSELL_ACCEPTED' : 'UPSELL_DECLINED', 'CUSTOMER');

            if (accepte) {
              setInstructions(`Upsell accepté : ${pendingUpsell.suggestedProductId}`);

              // INJECTION DANS LE PANIER AVEC PRIX PROMO
              const suggestedProd = products.find(p => p.id === pendingUpsell.suggestedProductId);
              if (suggestedProd) {
                const promoPrice = pendingUpsell.suggestedPrice || suggestedProd.price;

                dispatch(addToCart({
                  ...suggestedProd,
                  price: promoPrice,
                  quantity: 1,
                  name: `(PROMO) ${suggestedProd.name}`
                }));

                setExtractedData(prev => {
                  const updatedItems = [...prev.items, {
                    productId: suggestedProd.id,
                    productName: `(PROMO) ${suggestedProd.name}`,
                    size: suggestedProd.price,
                    price: promoPrice,
                    quantity: 1,
                    addOns: [],
                    specialInstructions: null,
                    error: []
                  }];
                  // PERSIST STEP
                  appendStep(transcriptFallback, { items: updatedItems }, 'SIMPLE_ORDER');
                  return { items: updatedItems };
                });
              }
            } else {
              setInstructions("Upsell refusé");
            }
          } catch (err) {
            console.error("Failed to track upsell event", err);
          }
          setPendingUpsell(null);
        } else if (factor) {
          // Suggestion issue d'un facteur (météo / paie / best-seller / nouveauté).
          // On enregistre uniquement l'événement : le produit est ajouté au panier par l'IA
          // via 'ajouter_produit_panier' au prix normal, on ne touche donc pas au panier ici.
          try {
            const userResponse = await axios.get('/api/v1/users/my-account');
            const subOrgId = userResponse.data?.subOrgId;
            const search = nom_produit ? String(nom_produit).toLowerCase() : '';
            const prod = search ? products.find(p => p.name?.toLowerCase().includes(search)) : null;

            await axios.post('/api/v1/upsell/events', {
              sessionId: sessionIdRef.current,
              organizationId: subOrgId,
              suggestedProductId: prod?.id || null,
              suggestedPrice: prod?.price || null,
              result: accepte ? 'ACCEPTED' : 'DECLINED',
              weatherContext: meteoContextRef.current || null,
              factor
            });

            appendStep(transcriptFallback, {}, accepte ? 'UPSELL_ACCEPTED' : 'UPSELL_DECLINED', 'CUSTOMER');
          } catch (err) {
            console.error("Failed to track factor upsell event", err);
          }
        }
        result = { status: "success" };
      } else if (name === "terminer_commande") {
        setInstructions("Validation de la commande en cours...");
        setIsConfirmingOrder(true);

        try {
          const userResponse = await axios.get('/api/v1/users/my-account');
          const subOrgId = userResponse.data?.subOrgId;

          // Skip end of order upsell

          // 2. Préparer le payload
          const finalSteps = [...steps, {
            sessionId: sessionIdRef.current,
            transcript: transcriptFallback || 'confirm_order',
            orderJson: JSON.stringify({ status: 'CONFIRMED', items: extractedData.items }),
            stepType: 'CONFIRMATION',
            speakerRole: 'CUSTOMER',
            latencyMs: 0,
            stepIndex: steps.length,
            createdAt: new Date().toISOString().replace('Z', '')
          }];

          const springPayload = {
            buyerId: user?.id || 'ff808181977e4e6401977e51a9c70001',
            organizationId: subOrgId,
            sessionId: sessionIdRef.current,
            sessionStartedAt: sessionStartedAtRef.current.replace('Z', ''),
            sessionEndedAt: new Date().toISOString().replace('Z', ''),
            isAbandoned: false,
            humanTakeover: false,
            orderSource: 'AI_VOICE',
            carPlate: carPlateRef.current,
            items: extractedData.items.map(item => ({
              productId: item.productId,
              size: String(item.size ?? ''),
              price: Number(item.price ?? 0),
              quantity: Number(item.quantity ?? 1),
              selections: item.selections, // Envoi des options pour le backend
            })),
            steps: finalSteps,
          };

          // 3. Envoyer la commande
          console.log('🚀 Envoi de la commande à Spring:', springPayload);
          await axios.post('/api/v1/orders', springPayload);
          console.log('✅ Commande confirmée et envoyée avec succès');

          // 4. Nettoyer l'état
          dispatch(resetCart());
          setExtractedData({ items: [] });
          setInstructions("Commande terminée et envoyée ! Merci.");
          result = { status: "success", message: "Commande enregistrée en base." };
        } catch (apiErr: any) {
          console.error("❌ Erreur lors de l'envoi de la commande:", apiErr);
          result = { status: "error", message: "Impossible de valider la commande." };
          setInstructions("Erreur de connexion au serveur.");
        } finally {
          setIsConfirmingOrder(false);
        }

        // Déconnexion du micro après le message d'au revoir, PUIS reset complet de l'état.
        // On ne dépend pas de l'événement WebSocket 'order-completed' (non émis dans le flux vocal direct),
        // sinon isOrderActive resterait à true et le mot de réveil ne se réarmerait jamais pour une nouvelle commande.
        setTimeout(() => {
          disconnect();
          setIsOrderActive(false);
          setPhase('idle');
        }, 5000);
      } else if (name === "request_agent") {
        // Step 1 — signal intent only; Gemini must ask the client to confirm before proceeding
        setInstructions("Demande agent reçue — en attente de confirmation client...");
        result = {
          status: "confirmation_needed",
          message: "Demande reçue. Demander au client : « Vous souhaitez bien être mis en relation avec un agent humain ? »"
        };
      } else if (name === "confirm_agent_request") {
        // Step 2 — client has answered; only trigger the switch if confirmed
        if (!args.confirme) {
          setInstructions("Demande agent annulée par le client.");
          result = { status: "cancelled", message: "Le client a annulé la demande. Reprise de la commande normale." };
        } else {
          setInstructions("Transfert vers un agent humain...");
          try {
            const userResponse = await axios.get('/api/v1/users/my-account');
            const subOrgId = userResponse.data?.subOrgId;

            const agentSteps = [...steps, {
              sessionId: sessionIdRef.current,
              transcript: transcriptFallback || 'demande agent humain confirmée',
              orderJson: JSON.stringify({ status: 'AGENT_REQUESTED' }),
              stepType: 'ESCALATION',
              speakerRole: 'CUSTOMER',
              latencyMs: 0,
              stepIndex: steps.length,
              createdAt: new Date().toISOString().replace('Z', '')
            }];

            const orderResp = await axios.post('/api/v1/orders', {
              buyerId: user?.id || 'ff808181977e4e6401977e51a9c70001',
              organizationId: subOrgId,
              sessionId: sessionIdRef.current,
              sessionStartedAt: sessionStartedAtRef.current.replace('Z', ''),
              sessionEndedAt: new Date().toISOString().replace('Z', ''),
              isAbandoned: false,
              humanTakeover: true,
              orderSource: 'AI_VOICE',
              items: extractedData.items.map(item => ({
                productId: item.productId,
                size: String(item.size ?? ''),
                price: Number(item.price ?? 0),
                quantity: Number(item.quantity ?? 1),
                selections: item.selections,
              })),
              steps: agentSteps,
            });

            const createdOrderId = orderResp?.data?.id;
            currentOrderIdRef.current = createdOrderId ?? null;

            if (createdOrderId) {
              await axios.patch(`/api/v1/voice-agent/orders/${createdOrderId}/request-agent`);
              // Publish snapshot 300ms after the PATCH so agents have time to subscribe
              setTimeout(() => {
                publishConversationSnapshot(sessionIdRef.current!, agentSteps);
              }, 300);
            }

            setIsAgentRequested(true);
            setPhase('agent_requested');
            setInstructions("Un agent va vous aider dans quelques instants. Veuillez patienter.");
            result = { status: "success", message: "Demande enregistrée. Un agent humain va vous prendre en charge." };

            window.dispatchEvent(new CustomEvent('voice-session-started', { detail: { sessionId: sessionIdRef.current, orderId: createdOrderId } }));

            agentTimerRef.current = setTimeout(async () => {
              if (currentOrderIdRef.current) {
                try {
                  await axios.patch(`/api/v1/voice-agent/orders/${currentOrderIdRef.current}/cancel-request`);
                } catch (e) {
                  console.warn('Cancel request failed:', e);
                }
              }
              setPhase('ai_resumed');
              setIsAgentRequested(false);
              setInstructions("Aucun agent disponible. Je reprends la commande avec vous.");
            }, 60000);
          } catch (apiErr: any) {
            console.error("❌ Erreur lors de la demande agent:", apiErr);
            result = { status: "error", message: "Impossible de contacter un agent pour le moment. Souhaitez-vous continuer avec l'IA ?" };
          }
        }
      } else if (name === "confirmer_repetition_commande") {
        const items = lastOrderDataRef.current?.orderedProducts || [];
        items.forEach((item: any) => {
          const product = item.product || {};
          dispatch(addToCart({
            ...product,
            id: product.id,
            name: product.name || item.productName || '',
            cover: product.cover || '',
            available: product.available ?? 1,
            price: item.unitPrice || product.price || 0,
            colors: product.colors || [],
            size: product.size || null,
            quantity: item.quantity || 1,
          }));
        });
        setExtractedData({
          items: items.map((item: any) => ({
            productId: item.product?.id || '',
            productName: item.product?.name || item.productName || '',
            size: item.unitPrice || 0,
            price: item.unitPrice || 0,
            quantity: item.quantity || 1,
            addOns: [],
            selections: item.trioSelections || [],
            specialInstructions: null,
            error: [],
          }))
        });
        setShowRepeatModal(false);
        setLastOrderData(null);
        setInstructions('Très bien, je remets la même commande !');
        result = { status: "success" };
      } else if (name === "annuler_repetition_commande") {
        setShowRepeatModal(false);
        setLastOrderData(null);
        setInstructions('D\'accord, je prends votre nouvelle commande !');
        result = { status: "success" };
      }
    } catch (err: any) {
      console.error("❌ Erreur pendant l'exécution de l'outil:", err);
      result = { status: "error", message: `Erreur interne: ${err.message}` };
    }

    // ENVOI DE LA RÉPONSE OFFICIELLE
    sendToolResponse([{
      id: (event.detail as any).id || event.detail.callId,
      name,
      response: { result }
    }]);
    }, [products, dispatch, disconnect, cart, extractedData, user?.id, appendStep, flushAiBuffer, steps, sendToolResponse, pendingUpsell, proposedRuleIds, trioOptionsMap, setShowRepeatModal, setLastOrderData, setExtractedData]); 

  const handleGeminiAISpeech = useCallback((event: any) => {
    const { text } = event.detail;
    if (!text) return;

    // Début d'un nouveau tour IA (buffer IA vide) : si le client a parlé sans déclencher
    // d'outil, on enregistre d'abord sa parole pour ne perdre aucun tour de la conversation.
    if (aiBufferRef.current === "" && !customerTurnCommittedRef.current) {
      const spoken = userSpeechBufferRef.current.trim();
      if (spoken) {
        appendStep(spoken, {}, 'SIMPLE_ORDER', 'CUSTOMER');
        customerTurnCommittedRef.current = true;
        userSpeechBufferRef.current = "";
      }
    }

    console.log(`AILYN dit (chunk): "${text}"`);

    // On accumule les morceaux pour éviter le découpage mot par mot
    aiBufferRef.current += (aiBufferRef.current ? " " : "") + text;

    if (aiBufferTimeoutRef.current) clearTimeout(aiBufferTimeoutRef.current);

    aiBufferTimeoutRef.current = setTimeout(() => {
      const fullText = aiBufferRef.current.trim();
      if (fullText) {
        console.log(`🎙️ AILYN dit (complet): "${fullText}"`);
        appendStep(fullText, extractedData, nextAiStepType, 'AI');
        // On réinitialise pour le prochain message
        setNextAiStepType('SIMPLE_ORDER');
        aiBufferRef.current = "";
      }
    }, 1000);
  }, [appendStep, extractedData, nextAiStepType]);

  const handleGeminiUserSpeech = useCallback((event: any) => {
    const { text } = event.detail;
    if (!text) return;

    // Si le tour précédent était déjà enregistré, l'arrivée de nouvelle parole = nouveau tour client.
    if (customerTurnCommittedRef.current) {
      customerTurnCommittedRef.current = false;
    }

    // On met à jour avec la dernière transcription (qui est cumulative pour le tour en cours)
    userSpeechBufferRef.current = text;

    // Afficher le popup de répétition au premier mot de l'utilisateur
    if (pendingRepeatRef.current && lastOrderDataRef.current) {
      pendingRepeatRef.current = false;
      setShowRepeatModal(true);
    }
  }, []);

  const handleAgentConnected = useCallback(async (event: any) => {
    if (agentTimerRef.current) {
      clearTimeout(agentTimerRef.current);
      agentTimerRef.current = null;
    }
    // Cut Gemini mic immediately — agent takes over the conversation
    disconnect();

    const name = event?.detail?.agentName || 'Agent';
    setAgentName(name);
    setPhase('agent_connected');
    setInstructions("L'agent vous prend en charge.");

    try {
      const tokenResp = await axios.post(
        `/api/v1/voice-agent/livekit-token?roomName=${sessionIdRef.current}&participantName=kiosk`
      );
      const { token, url } = tokenResp.data;
      const room = new Room();
      livekitRoomRef.current = room;
      room.on(RoomEvent.ActiveSpeakersChanged, (speakers: any[]) => setIsSpeaking(speakers.length > 0));
      room.on(RoomEvent.Disconnected, () => {
        livekitRoomRef.current = null;
        setIsSpeaking(false);
      });
      room.on(RoomEvent.TrackSubscribed, (track: any) => {
        if (track.kind === 'audio') document.body.appendChild(track.attach());
      });
      room.on(RoomEvent.TrackUnsubscribed, (track: any) => {
        track.detach();
      });
      await room.connect(url, token);
      await room.localParticipant.setMicrophoneEnabled(true);
    } catch (err) {
      console.warn('LiveKit connection failed:', err);
    }
  }, [disconnect]);

  const handleAgentReleased = useCallback(() => {
    setPhase('ai_resumed');
    setAgentName('');
    setIsAgentRequested(false);
    setInstructions("L'agent vous a rendu la main. Je reprends avec vous !");
    if (livekitRoomRef.current) {
      livekitRoomRef.current.disconnect();
      livekitRoomRef.current = null;
    }
    setIsSpeaking(false);
    // Restart Gemini after a short delay so UI can transition
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('gemini-resume-after-agent'));
    }, 1500);
  }, []);

  useEffect(() => () => {
    if (livekitRoomRef.current) {
      livekitRoomRef.current.disconnect();
      livekitRoomRef.current = null;
    }
  }, []);

  // Refresh cart from backend when agent modifies the order
  const handleOrderUpdated = useCallback(async (event: any) => {
    const { orderId } = (event as CustomEvent).detail || {};
    if (!orderId) return;
    try {
      const res = await axios.get(`/api/v1/orders/${orderId}`);
      const orderedProducts: any[] = res.data?.orderedProducts || [];
      const items = orderedProducts.map((op: any) => ({
        productId: op.product?.id || '',
        productName: op.product?.name ||
          op.product?.translations?.find((t: any) => t.language === 'FR')?.name ||
          op.product?.translations?.find((t: any) => t.language === 'EN')?.name || 'Produit',
        size: 0,
        price: Number(op.unitPrice ?? op.product?.price ?? 0),
        quantity: Number(op.quantity ?? 1),
        addOns: [],
        selections: op.trioSelections || [],
        specialInstructions: null,
        error: []
      }));
      setExtractedData({ items });
    } catch (err) {
      console.warn('order-updated: failed to refresh cart', err);
    }
  }, []);

  // Mark order as confirmed when agent sends it to POS
  const handleOrderCompleted = useCallback(() => {
    setPhase('confirming');
    setInstructions("Votre commande a été envoyée au POS ! Merci, passez à la fenêtre suivante.");
    if (livekitRoomRef.current) {
      livekitRoomRef.current.disconnect();
      livekitRoomRef.current = null;
    }
    setIsSpeaking(false);
    setTimeout(() => {
      disconnect();
      setIsOrderActive(false);
      setExtractedData({ items: [] });
      setPhase('idle');
    }, 8000);
  }, [disconnect]);

  useEffect(() => {
    window.addEventListener('gemini-function-call', handleGeminiToolCall);
    window.addEventListener('gemini-ai-speech', handleGeminiAISpeech);
    window.addEventListener('gemini-user-speech', handleGeminiUserSpeech as any);
    window.addEventListener('agent-connected', handleAgentConnected);
    window.addEventListener('agent-released', handleAgentReleased);
    window.addEventListener('order-updated', handleOrderUpdated as EventListener);
    window.addEventListener('order-completed', handleOrderCompleted);
    const handleResumeAfterAgent = () => handleWakeWordRef.current();
    window.addEventListener('gemini-resume-after-agent', handleResumeAfterAgent);
    return () => {
      window.removeEventListener('gemini-function-call', handleGeminiToolCall);
      window.removeEventListener('gemini-ai-speech', handleGeminiAISpeech);
      window.removeEventListener('gemini-user-speech', handleGeminiUserSpeech as any);
      window.removeEventListener('agent-connected', handleAgentConnected);
      window.removeEventListener('agent-released', handleAgentReleased);
      window.removeEventListener('order-updated', handleOrderUpdated as EventListener);
      window.removeEventListener('order-completed', handleOrderCompleted);
      window.removeEventListener('gemini-resume-after-agent', handleResumeAfterAgent);
    };
  }, [handleGeminiToolCall, handleGeminiAISpeech, handleGeminiUserSpeech, handleAgentConnected, handleAgentReleased, handleOrderUpdated, handleOrderCompleted]);

  const handleWakeWord = useCallback(async () => {
    console.log(`🎯 Mot de réveil détecté!`);
    stopListeningRef.current();
    setIsOrderActive(true);
    const now = new Date().toISOString().replace('Z', '');
    const sessId = crypto?.randomUUID?.() ?? `${Date.now()}`;

    sessionIdRef.current = sessId;
    sessionStartedAtRef.current = now;
    // carPlateRef.current = `PLATECOMMANDE-${Date.now()}`; // Unique — remettre quand plaque réelle disponible
    carPlateRef.current = 'TEST-PLATE-001'; // Pour tester la feature "répéter commande"

    // On enregistre IMMÉDIATEMENT le début de la session
    // On sème uniquement le tour client "commencer la commande". Le message d'accueil de l'IA
    // est capté en temps réel depuis Gemini (ne pas le semer en dur pour éviter un doublon).
    const startSteps = [
      {
        sessionId: sessId,
        transcript: "commencer la commande",
        orderJson: '{"status":"STARTED"}',
        stepType: 'SIMPLE_ORDER',
        speakerRole: 'CUSTOMER',
        stepIndex: 0,
        latencyMs: 0,
        createdAt: now
      }
    ];

    setSteps(startSteps);
    setIsAgentRequested(false);
    userSpeechBufferRef.current = "";
    aiBufferRef.current = "";
    customerTurnCommittedRef.current = false;
    if (aiBufferTimeoutRef.current) clearTimeout(aiBufferTimeoutRef.current);
    setInstructions("Démarrage de l'assistant Gemini...");

    // Pre-load upsell rules + contexte météo + jour de paie + best-sellers (réutilise le même appel my-account)
    meteoContextRef.current = "";
    let isPayday = false;
    let bestSellers: string[] = [];
    let newProducts: string[] = [];
    let freshVoiceName: string = user?.voiceName || 'Aoede';
    try {
      const userResp = await axios.get('/api/v1/users/my-account');
      const subOrgId = userResp.data?.subOrgId;
      freshVoiceName = userResp.data?.voiceName || freshVoiceName;

      if (subOrgId) {
        // Règles d'upsell (fire-and-forget, ne bloque pas le démarrage)
        axios.get(`/api/v1/upsell/rules/${subOrgId}`)
          .then(rulesResp => setUpsellRules(rulesResp.data))
          .catch(() => { /* règles indisponibles, on continue */ });

        // Météo de la ville de la sous-org (timeout court, échec silencieux)
        try {
          const weatherResp = await axios.get(`/api/v1/upsell/weather/${subOrgId}`, { timeout: 2500 });
          if (weatherResp.data && weatherResp.data.description) {
            meteoContextRef.current = `${weatherResp.data.description}, ${Math.round(weatherResp.data.temperature)}°C`;
          }
        } catch (e) {
          console.warn('Météo indisponible, démarrage sans contexte météo.');
        }

        // Jour de paie (jeudi au Canada). SIMULATE_PAYDAY=true pour tester hors jeudi.
        try {
          const paydayResp = await axios.get(`/api/v1/upsell/payday/${subOrgId}`, {
            params: { simulateThursday: SIMULATE_PAYDAY }, timeout: 2500
          });
          isPayday = !!paydayResp.data?.payday;
        } catch (e) {
          console.warn('Statut jour de paie indisponible.');
        }

        // Best-sellers récents (top 3 sur la fenêtre configurée)
        try {
          const bestResp = await axios.get(`/api/v1/upsell/bestseller/${subOrgId}`, {
            params: { hours: BESTSELLER_HOURS }, timeout: 2500
          });
          if (Array.isArray(bestResp.data?.products)) {
            bestSellers = bestResp.data.products;
          }
        } catch (e) {
          console.warn('Best-sellers indisponibles.');
        }

        // Nouveaux produits (ajoutés récemment au menu)
        try {
          const newResp = await axios.get(`/api/v1/upsell/new-products/${subOrgId}`, {
            params: { days: NEW_PRODUCT_DAYS }, timeout: 2500
          });
          if (Array.isArray(newResp.data?.products)) {
            newProducts = newResp.data.products;
          }
        } catch (e) {
          console.warn('Nouveaux produits indisponibles.');
        }
      }
    } catch (e) {
      console.warn('Impossible de récupérer le compte utilisateur pour le préchargement.');
    }

    // Génération du menu formaté en texte pour le prompt de l'IA
    const menuDispo = products.map((p: any) => `- ${p.name} (${p.price} $)`).join('\n');

    // Bloc météo injecté dans le prompt (vide si la météo est indisponible)
    const meteoBlock = meteoContextRef.current
      ? `\nMÉTÉO ACTUELLE : ${meteoContextRef.current}.
SUGGESTION MÉTÉO (à faire une seule fois) : Au cours de la commande (juste après l'accueil, ou après le premier article ajouté), tu DOIS proposer UNE fois, de façon naturelle et chaleureuse, un produit du menu qui correspond à cette température et ces conditions : par temps chaud, quelque chose de frais ; par temps froid ou pluvieux, quelque chose de chaud ou réconfortant. Choisis toi-même le produit le plus adapté parmi le menu existant. Un seul produit, une seule fois, sans insister si le client refuse.\n`
      : '';

    // Bloc jour de paie injecté dans le prompt (vide si ce n'est pas un jour de paie)
    const paydayBlock = isPayday
      ? `\nJOUR DE PAIE : c'est aujourd'hui jour de paie. Une seule fois pendant la commande, propose de façon naturelle un produit premium, un upgrade de taille, ou un dessert — choisis toi-même le plus pertinent dans le menu existant. Un seul produit, sans insister si le client refuse.\n`
      : '';

    // Bloc best-seller injecté dans le prompt (vide si aucune vente récente)
    const bestSellerBlock = bestSellers.length > 0
      ? `\nPRODUITS POPULAIRES EN CE MOMENT : ${bestSellers.join(', ')}.\nSi le client hésite ou demande une recommandation, mets en avant l'un de ces produits comme étant très populaire en ce moment ("c'est notre best-seller du moment"). Reste naturelle, une seule fois.\n`
      : '';

    // Bloc nouveautés injecté dans le prompt (vide si aucun nouveau produit)
    const newProductBlock = newProducts.length > 0
      ? `\nNOUVEAUTÉS AU MENU : ${newProducts.join(', ')}.\nUne seule fois pendant la commande, invite naturellement le client à essayer une de ces nouveautés ("nous avons une nouveauté, le/la ${newProducts[0]}, ça vous tente ?"). Un seul produit, sans insister si le client refuse.\n`
      : '';

    // Le cerveau de l'IA : Son Rôle + Ses Directives + Le Menu Réel
    let systemPrompt = `Tu es AI-LYN, une assistante vocale virtuelle au drive d'un restaurant rapide.
Ton rôle est de prendre les commandes des clients de manière fluide, polie et très concise (parle au maximum 1 à 2 phrases par réponse).
Tu communiques en français par defaut mais tu t'adapte à la langue du client qui peut etre en anglais ou autre.

Voici le MENU ACTUEL du restaurant avec les prix :
${menuDispo}
${meteoBlock}${paydayBlock}${bestSellerBlock}${newProductBlock}
DIRECTIVES IMPORTANTES :
1. Dès que la connexion s'ouvre, accueille le client avec enthousiasme (ex: "Bonjour ! Bienvenue au drive. Que puis-je vous servir aujourd'hui ?").
2. Si le client demande un produit qui n'est pas dans le menu ci-dessus, dis-lui poliment que vous ne l'avez pas et propose une alternative du menu.
3. SYSTÉMATIQUE : Pour CHAQUE produit demandé par le client, utilise TOUJOURS l'outil 'verifier_options_produit' AVANT de l'ajouter au panier. Cela est crucial pour vérifier s'il existe des options obligatoires (cuisson, boisson, accompagnement) ou des suppléments.
4. Si 'verifier_options_produit' retourne des options, présente-les naturellement au client et attends son choix. Une fois le produit et ses options confirmés, UTILISE 'ajouter_produit_panier'.
5. Si le client veut ENLEVER un produit ou RÉDUIRE la quantité d'un produit déjà commandé, UTILISE 'supprimer_produit_panier'.
6. Après chaque action, demande toujours s'il souhaite autre chose.
7. Quand le client dit qu'il a terminé, UTILISE L'OUTIL 'terminer_commande' pour clôturer et envoyer la commande au serveur.
8. INTERVENTION HUMAINE (OBLIGATOIRE — 2 étapes) :
   a. Si le client exprime clairement qu'il veut parler à un humain/agent/employé, appelle D'ABORD 'request_agent', puis demande confirmation : « Vous souhaitez bien être mis en relation avec un agent humain ? »
   b. Selon la réponse : appelle 'confirm_agent_request(confirme: true)' si oui → dis "Parfait, je vous transfère !"; appelle 'confirm_agent_request(confirme: false)' si non → dis "D'accord, je continue de vous aider !"
   c. Ne jamais déclencher le transfert sans avoir obtenu cette confirmation explicite.
9. DIRECTIVES D'UPSELL (ESSENTIEL) :
   - Lorsque tu reçois un objet "suggestion_upsell" dans le résultat d'un outil, tu DOIS le proposer immédiatement au client.
   - Si le client accepte la suggestion (ex: "Oui", "D'accord") :
     a. Appelle UNIQUEMENT 'tracker_reponse_upsell(accepte: true)'.
     b. NE PAS appeler 'ajouter_produit_panier' pour ce produit, car l'outil de tracking s'en occupe automatiquement avec le prix promo.
   - Si le client refuse (ex: "Non", "C'est tout") :
     a. Appelle 'tracker_reponse_upsell(accepte: false)'.
   - Si un outil retourne "status": "hold", cela signifie que tu DOIS traiter la suggestion avant de pouvoir finaliser. Propose l'upsell, attend la réponse du client, traite-la avec les outils ci-dessus, puis rappelle 'terminer_commande' si le client veut toujours finir.
   - Ne propose jamais de produit qui n'est pas explicitement mentionné dans "suggestion_upsell".
10. SUIVI DES SUGGESTIONS CONTEXTUELLES (météo, jour de paie, best-seller, nouveauté) :
   - Ces suggestions viennent des sections ci-dessus (MÉTÉO ACTUELLE, JOUR DE PAIE, PRODUITS POPULAIRES, NOUVEAUTÉS), PAS d'un objet "suggestion_upsell".
   - Dès que le client répond à l'une de ces suggestions, appelle 'tracker_reponse_upsell' avec : accepte (true/false), factor (WEATHER, PAYDAY, BESTSELLER ou NEW_PRODUCT) et nom_produit (le produit suggéré).
   - Si le client accepte, ajoute aussi le produit normalement avec 'ajouter_produit_panier' (prix normal) — ici le tracking n'ajoute PAS le produit au panier.

Sois naturelle, réactive et focalisée sur la prise de commande. Tu es efficace et tu aides le client à faire les meilleurs choix.`;

    // Vérification de la dernière commande avant de connecter Gemini
    let lastOrderFound = false;
    let lastOrderRecap = '';
    try {
      const response = await axios.get(`/api/v1/orders/last-by-plate`, {
        params: { carPlate: carPlateRef.current }
      });
      if (response.status === 200 && response.data) {
        setLastOrderData(response.data);
        pendingRepeatRef.current = true;
        const items = response.data.orderedProducts || [];
        lastOrderRecap = items.map((i: any) =>
          `${i.quantity || 1}x ${i.product?.name || i.productName}`
        ).join(', ');
        lastOrderFound = true;
      }
    } catch (err) {
      console.log('ℹ️ Aucune commande précédente trouvée pour cette plaque');
    }

    if (lastOrderFound) {
      systemPrompt += `\n\nNOTE COMMANDE PRÉCÉDENTE :
Le client (plaque: ${carPlateRef.current}) a déjà commandé : ${lastOrderRecap}.
Accueille-le et demande-lui : "Vous aviez commandé [${lastOrderRecap}] la dernière fois. Voulez-vous la même chose ?"
S'il dit oui : utilise l'outil 'confirmer_repetition_commande', puis réponds "Parfait ! Je remets la même commande !"
S'il dit non : utilise l'outil 'annuler_repetition_commande', puis dis "D'accord, que puis-je vous servir ?" et continue normalement.`;
    }

    connect([{
      functionDeclarations: [
        {
          name: "verifier_options_produit",
          description: "Vérifie si un produit a des options (comme le choix d'une boisson ou frites pour un Trio) AVANT de l'ajouter au panier.",
          parameters: {
            type: "OBJECT",
            properties: {
              nom_produit: { type: "STRING", description: "Le nom du produit à vérifier" }
            },
            required: ["nom_produit"]
          }
        },
        {
          name: "ajouter_produit_panier",
          description: "Ajoute un produit au panier. Doit être appelé avec la quantité, et si le produit avait des options, avec la liste des options choisies.",
          parameters: {
            type: "OBJECT",
            properties: {
              nom_produit: { type: "STRING", description: "Le nom du produit tel qu'il apparaît dans le menu (ex: burger, frites)" },
              quantite: { type: "INTEGER", description: "La quantité souhaitée (par défaut 1)" },
              options_choisies: {
                type: "ARRAY",
                description: "Liste des options choisies par le client (ex: boisson, frites).",
                items: {
                  type: "OBJECT",
                  properties: {
                    id: { type: "STRING", description: "L'ID de l'option (fourni par verifier_options_produit)" },
                    nom_option: { type: "STRING", description: "Le nom de l'option (ex: Coca Cola)" }
                  }
                }
              }
            },
            required: ["nom_produit", "quantite"]
          }
        },
        {
          name: "supprimer_produit_panier",
          description: "Supprime un produit du panier ou réduit sa quantité.",
          parameters: {
            type: "OBJECT",
            properties: {
              nom_produit: { type: "STRING", description: "Le nom du produit à supprimer" },
              quantite: { type: "INTEGER", description: "La quantité à retirer (par défaut 1)" }
            },
            required: ["nom_produit", "quantite"]
          }
        },
        {
          name: "consulter_panier",
          description: "Consulte le contenu actuel du panier et calcule le sous-total. Utile pour résumer la commande au client avant de la terminer.",
          parameters: { type: "OBJECT", properties: {} }
        },
        {
          name: "terminer_commande",
          description: "Confirme, valide et envoie la commande au serveur lorsque le client n'a plus rien à ajouter.",
          parameters: { type: "OBJECT", properties: {} }
        },
        {
          name: "tracker_reponse_upsell",
          description: "Enregistre si le client a accepté ou refusé une suggestion d'upsell. À appeler immédiatement après la réponse du client à TOUTE suggestion : règle classique, OU suggestion liée à la météo, au jour de paie, à un produit populaire (best-seller) ou à une nouveauté.",
          parameters: {
            type: "OBJECT",
            properties: {
              accepte: { type: "BOOLEAN", description: "Vrai si le client a accepté, faux s'il a refusé" },
              factor: { type: "STRING", description: "Origine de la suggestion : WEATHER (météo), PAYDAY (jour de paie), BESTSELLER (produit populaire), NEW_PRODUCT (nouveauté). À omettre pour une suggestion de règle classique." },
              nom_produit: { type: "STRING", description: "Nom du produit suggéré (obligatoire quand 'factor' est fourni, pour identifier le produit)." }
            },
            required: ["accepte"]
          }
        },
        {
          name: "request_agent",
          description: "Première étape : à appeler quand le client exprime clairement le souhait de parler à un employé réel. Cette étape NE déclenche PAS le transfert — elle te demande d'obtenir la confirmation du client avant d'aller plus loin.",
          parameters: { type: "OBJECT", properties: {} }
        },
        {
          name: "confirm_agent_request",
          description: "Deuxième étape : à appeler après avoir demandé confirmation au client. Si le client confirme (oui), le transfert est déclenché. Sinon (non), la commande reprend normalement.",
          parameters: {
            type: "OBJECT",
            properties: {
              confirme: { type: "BOOLEAN", description: "Vrai si le client a confirmé vouloir un agent, faux s'il a changé d'avis" }
            },
            required: ["confirme"]
          }
        },
        {
          name: "confirmer_repetition_commande",
          description: "Appelle cet outil quand le client dit OUI à la question de répéter sa dernière commande. Cela ajoute les produits au panier et ferme le récapitulatif.",
          parameters: { type: "OBJECT", properties: {} }
        },
        {
          name: "annuler_repetition_commande",
          description: "Appelle cet outil quand le client dit NON à la question de répéter sa dernière commande. Cela ferme le récapitulatif et continue normalement.",
          parameters: { type: "OBJECT", properties: {} }
        }
      ]
    }], systemPrompt, freshVoiceName); // voiceName frais récupéré depuis my-account au démarrage
  }, [connect, products, user?.voiceName]);

  const { stopListening } = useWakeWord(handleWakeWord, isEnabled && !isOrderActive);
  useEffect(() => { stopListeningRef.current = stopListening; }, [stopListening]);
  useEffect(() => { handleWakeWordRef.current = handleWakeWord; }, [handleWakeWord]);

  useEffect(() => {
    if (!isEnabled) {
      if (isOrderActive || isConnected) {
        disconnect();
        setIsOrderActive(false);
      }
      setInstructions("Dites 'Commencer la commande' pour activer l'assistant.");
    }
  }, [isEnabled, isOrderActive, isConnected, disconnect]);

  useEffect(() => {
    setIsProcessingOrder(isProcessing);
    if (isConnected) {
      setInstructions("Assistant actif. Je vous écoute !");
      setAccumulatedTranscript("... Communication Live ...");
    }
  }, [isConnected, isProcessing]);

  const checkLastOrder = useCallback(async () => {
    console.log('🔍 [checkLastOrder] Checking last order for plate:', carPlateRef.current);
    try {
      const response = await axios.get(`/api/v1/orders/last-by-plate`, {
        params: { carPlate: carPlateRef.current }
      });
      console.log('🔍 [checkLastOrder] Response status:', response.status, 'data:', response.data);
      if (response.status === 200 && response.data) {
        console.log('✅ [checkLastOrder] Order found, setting lastOrderData');
        setLastOrderData(response.data);
        console.log('🔍 [checkLastOrder] Setting showRepeatModal to true');
        setShowRepeatModal(true);
      } else {
        console.log('⚠️ [checkLastOrder] No order found (status:', response.status, ')');
      }
    } catch (err) {
      console.log('❌ [checkLastOrder] Error fetching last order:', err);
    }
  }, []);

  const acceptLastOrder = useCallback(() => {
    if (!lastOrderData) return;
    const items = lastOrderData.orderedProducts || [];
    items.forEach((item: any) => {
      const product = item.product || {};
      dispatch(addToCart({
        ...product,
        id: product.id,
        name: product.name || item.productName || '',
        cover: product.cover || '',
        available: product.available ?? 1,
        price: item.unitPrice || product.price || 0,
        colors: product.colors || [],
        size: product.size || null,
        quantity: item.quantity || 1,
      }));
    });
    setExtractedData({
      items: items.map((item: any) => ({
        productId: item.product?.id || '',
        productName: item.product?.name || item.productName || '',
        size: item.unitPrice || 0,
        price: item.unitPrice || 0,
        quantity: item.quantity || 1,
        addOns: [],
        selections: item.trioSelections || [],
        specialInstructions: null,
        error: [],
      }))
    });
    setShowRepeatModal(false);
    setLastOrderData(null);
    setInstructions('Très bien, je vous remets la même commande que la dernière fois !');
  }, [lastOrderData, dispatch]);

  const declineLastOrder = useCallback(() => {
    setShowRepeatModal(false);
    setLastOrderData(null);
    setInstructions('D\'accord, je prends votre nouvelle commande !');
  }, []);

  return {
    isOrderActive,
    isProcessingOrder,
    isConfirmingOrder,
    isAgentRequested,
    agentName,
    isSpeaking,
    phase,
    trioSelection,
    currentTranscript,
    accumulatedTranscript,
    loadingDots: 0,
    instructions,
    extractedData,
    trioItems,
    removeSoloItem: (productId: string) => {
      dispatch(decreaseQuantity(productId));
      setExtractedData(prev => ({ items: prev.items.filter(i => i.productId !== productId) }));
    },
    removeTrioItem: (productId: string) => { },
    carPlate: carPlateRef.current,
    lastOrderData,
    showRepeatModal,
    checkLastOrder,
    acceptLastOrder,
    declineLastOrder,
    manualStart: () => handleWakeWord(),
    manualStop: () => { disconnect(); setIsOrderActive(false); }
  };
}